'use client';

import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';

type ScriptPayload = {
  type?: string;
  src?: string;
  async?: boolean;
  defer?: boolean;
  text?: string;
};

type LandingPageEmbedProps = {
  rawHtml: string;
  embedLink: string;
  className?: string;
};

function isRelativeAssetUrl(value: string) {
  if (!value || value.startsWith('#')) {
    return false;
  }

  return (
    !value.startsWith('http://') &&
    !value.startsWith('https://') &&
    !value.startsWith('//') &&
    !value.startsWith('data:') &&
    !value.startsWith('mailto:') &&
    !value.startsWith('tel:') &&
    !value.startsWith('javascript:') &&
    !value.startsWith('blob:')
  );
}

function rewriteRelativeUrl(url: string, base: URL) {
  return new URL(url, base).toString();
}

function rewriteSrcset(value: string, base: URL) {
  return value
    .split(',')
    .map((candidate) => candidate.trim())
    .filter(Boolean)
    .map((candidate) => {
      const [url, ...descriptorParts] = candidate.split(/\s+/);
      if (!url || !isRelativeAssetUrl(url)) {
        return candidate;
      }

      return `${rewriteRelativeUrl(url, base)}${descriptorParts.length > 0 ? ` ${descriptorParts.join(' ')}` : ''}`;
    })
    .join(', ');
}

function rewriteStyleUrls(value: string, base: URL) {
  return value.replace(/url\((['"]?)(.*?)\1\)/gi, (_, quote, rawUrl) => {
    if (!rawUrl || !isRelativeAssetUrl(rawUrl)) {
      return `url(${quote}${rawUrl}${quote})`;
    }

    return `url(${quote}${rewriteRelativeUrl(rawUrl, base)}${quote})`;
  });
}

/**
 * `:root` always targets the top-level document's root element, never the
 * shadow root — inside a shadow tree it matches nothing, so `:root { --x }`
 * custom-property declarations from the embedded page silently stop applying
 * once their CSS moves into our shadow root. `:host` is the shadow-scoped
 * equivalent, so root-level variables still cascade into the embed content.
 */
function rewriteRootSelector(css: string) {
  return css.replace(/:root\b/gi, ':host');
}

async function fetchStylesheetText(url: string) {
  try {
    const response = await fetch(url, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    return await response.text();
  } catch {
    // Most likely a cross-origin stylesheet without CORS headers — leave its
    // <link> tag as-is so the browser still loads it normally; it just won't
    // get the :root -> :host rewrite.
    return null;
  }
}

/**
 * Parses and rewrites the fetched page client-side with the browser's native
 * DOMParser, instead of the Node-only `jsdom` package. `jsdom`'s dependency
 * chain (html-encoding-sniffer -> @exodus/bytes) ships an ESM-only module
 * that breaks under some serverless bundlers (`ERR_REQUIRE_ESM`), and this
 * component already only runs client-side (it drives a shadow root).
 */
async function extractHtmlAndScripts(rawHtml: string, baseUrl: string) {
  const document = new DOMParser().parseFromString(rawHtml, 'text/html');
  const baseTag = document.querySelector('base');
  const baseHref = baseTag?.getAttribute('href');
  const base = baseHref ? new URL(baseHref, baseUrl) : new URL(baseUrl);

  baseTag?.remove();

  const elements = document.querySelectorAll(
    '[src],[href],[srcset],[style],style,link[href],img[src],script[src],source[src],video[src],audio[src],source[srcset],img[srcset]'
  );

  for (const element of elements) {
    const src = element.getAttribute('src');
    const href = element.getAttribute('href');
    const srcset = element.getAttribute('srcset');
    const style = element.getAttribute('style');

    if (src && isRelativeAssetUrl(src)) {
      element.setAttribute('src', rewriteRelativeUrl(src, base));
    }

    if (href && isRelativeAssetUrl(href)) {
      element.setAttribute('href', rewriteRelativeUrl(href, base));
    }

    if (srcset) {
      element.setAttribute('srcset', rewriteSrcset(srcset, base));
    }

    if (style) {
      element.setAttribute('style', rewriteStyleUrls(style, base));
    }
  }

  for (const styleEl of Array.from(document.querySelectorAll('style'))) {
    styleEl.textContent = rewriteRootSelector(rewriteStyleUrls(styleEl.textContent ?? '', base));
  }

  const linkStylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'));

  await Promise.all(
    linkStylesheets.map(async (linkEl) => {
      const href = linkEl.getAttribute('href');

      if (!href) {
        return;
      }

      const cssText = await fetchStylesheetText(href);

      if (cssText === null) {
        return;
      }

      const styleEl = document.createElement('style');
      styleEl.textContent = rewriteRootSelector(rewriteStyleUrls(cssText, new URL(href)));
      linkEl.replaceWith(styleEl);
    })
  );

  const scripts: ScriptPayload[] = Array.from(document.querySelectorAll('script')).flatMap((script) => {
    const src = script.getAttribute('src');

    const nextScript: ScriptPayload = {
      type: script.getAttribute('type') ?? undefined,
      src: src && isRelativeAssetUrl(src) ? rewriteRelativeUrl(src, base) : (src ?? undefined),
      async: script.hasAttribute('async') ? true : undefined,
      defer: script.hasAttribute('defer') ? true : undefined,
      text: script.textContent ?? undefined,
    };

    script.remove();

    return [nextScript];
  });

  return {
    html: `${document.head ? document.head.innerHTML : ''}${document.body ? document.body.innerHTML : ''}`,
    scripts,
  };
}

function LandingPageEmbedLoader() {
  return (
    <div className='flex h-full min-h-[480px] w-full flex-col items-center justify-center gap-4 px-6'>
      <div className='h-4 w-2/3 max-w-sm animate-pulse rounded bg-current/10' />
      <div className='h-4 w-1/2 max-w-xs animate-pulse rounded bg-current/10' />
      <div className='mt-2 h-10 w-40 animate-pulse rounded-full bg-current/10' />
    </div>
  );
}

/**
 * Embed scripts (e.g. HubSpot's `hbspt.forms.create({ target: '#id' })`) call
 * `document.getElementById`/`querySelector` expecting a flat document, but
 * their target lives inside our shadow root, invisible to those APIs. Some
 * widgets resolve their target asynchronously — HubSpot's forms-v2.js fetches
 * the form definition over the network before it ever looks up the DOM node —
 * so the redirect has to stay installed for the embed's whole mounted
 * lifetime, not just around one script's synchronous execution.
 *
 * Installs are removed via compare-and-swap so multiple embeds on the page,
 * or an out-of-order unmount, can't clobber a still-active patch installed
 * after this one.
 */
function installShadowDomDocumentPatch(shadow: ShadowRoot) {
  const prevGetElementById = document.getElementById;
  const prevQuerySelector = document.querySelector;
  const prevQuerySelectorAll = document.querySelectorAll;

  const patchedGetElementById: typeof document.getElementById = (id) =>
    shadow.getElementById(id) ?? prevGetElementById.call(document, id);
  const patchedQuerySelector = ((selectors: string) =>
    shadow.querySelector(selectors) ?? prevQuerySelector.call(document, selectors)) as typeof document.querySelector;
  const patchedQuerySelectorAll = ((selectors: string) => {
    const shadowMatches = shadow.querySelectorAll(selectors);
    return shadowMatches.length > 0 ? shadowMatches : prevQuerySelectorAll.call(document, selectors);
  }) as typeof document.querySelectorAll;

  document.getElementById = patchedGetElementById;
  document.querySelector = patchedQuerySelector;
  document.querySelectorAll = patchedQuerySelectorAll;

  return function uninstall() {
    if (document.getElementById === patchedGetElementById) {
      document.getElementById = prevGetElementById;
    }
    if (document.querySelector === patchedQuerySelector) {
      document.querySelector = prevQuerySelector;
    }
    if (document.querySelectorAll === patchedQuerySelectorAll) {
      document.querySelectorAll = prevQuerySelectorAll;
    }
  };
}

export { installShadowDomDocumentPatch, extractHtmlAndScripts };

export default function LandingPageEmbed({ rawHtml, embedLink, className }: LandingPageEmbedProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  const [isReady, setIsReady] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const shadow = shadowRef.current ?? container.attachShadow({ mode: 'open' });
    shadowRef.current = shadow;

    let cancelled = false;
    let uninstallPatch: (() => void) | null = null;

    async function run() {
      const { html, scripts } = await extractHtmlAndScripts(rawHtml, embedLink);

      if (cancelled) {
        return;
      }

      shadow.innerHTML = html;
      setIsReady(true);
      uninstallPatch = installShadowDomDocumentPatch(shadow);

      for (const script of scripts) {
        if (cancelled) {
          return;
        }

        const nextScript = document.createElement('script');

        if (script.type) {
          nextScript.type = script.type;
        }

        if (script.defer) {
          nextScript.defer = true;
        }

        if (script.src) {
          nextScript.src = script.src;

          if (script.async) {
            // Author explicitly opted out of ordering — fire and forget.
            nextScript.async = true;
            shadow.appendChild(nextScript);
            continue;
          }

          // Dynamically-inserted <script src> elements run async by default,
          // unlike parser-inserted ones — force in-order execution so a
          // library loader (e.g. HubSpot's forms.js) finishes before the
          // inline script that depends on it runs.
          nextScript.async = false;
          await new Promise<void>((resolve) => {
            nextScript.addEventListener('load', () => resolve());
            nextScript.addEventListener('error', () => resolve());
            shadow.appendChild(nextScript);
          });
          continue;
        }

        if (script.text) {
          nextScript.textContent = script.text;
          shadow.appendChild(nextScript);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
      uninstallPatch?.();
    };
  }, [rawHtml, embedLink]);

  return (
    <div className={clsx('relative min-h-[480px] w-full', className)}>
      {!isReady && (
        <div className='absolute inset-0'>
          <LandingPageEmbedLoader />
        </div>
      )}
      <div
        ref={containerRef}
        className={clsx('w-full transition-opacity duration-300', isReady ? 'opacity-100' : 'opacity-0')}
      />
    </div>
  );
}
